import React from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import ReactHookFormSwitch from "components/reactHookFormComponents/ReactHookFormSwitch";
import {
    RECURRING_OFFSET_TYPE,
    RECURRING_ORDINAL_WEEKDAY_TYPE,
    WEEKDAY
} from 'config/CustomEnums'
import { CustomTitleWithRadio } from 'components/earning/CustomBaseComponments';
import { DropDownWithDefaultSelect } from 'components/base/ActionsDropdown';
import { getOrdinalSuffix } from "models/CreateCampaignModel"
import CustomTips from "components/base/CustomTips";
import { ReactHookFormErrorMessage } from 'components/base/ErrorFieldMessage';
import './RecurringCampaignPeriodSelector.scss'

const recurringRadioOptionTypes = {
    "OFFSET_BY_RELATIVE": "OFFSET_BY_RELATIVE",
    "OFFSET_WEEKDAY": "OFFSET_WEEKDAY",
    "OFFSET_DAY_IN_MONTH": "OFFSET_DAY_IN_MONTH",
}

const relativeTypes = [
    {
        "name": "Days",
        "value": RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_DAY
    },
    {
        "name": "Weeks",
        "value": RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_WEEK
    },
    {
        "name": "Months",
        "value": RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_MONTH
    },
]

const offsetTypeWords = {
    start: {
        "title": "Start date",
        "tips": "The default start time of the selected start date is 00:00.",
        "relativeTypeSuffix": "before the first day of the birth month",
        "offsetInMonthSuffix": "month(s) before the first day of the birth month",
        "offsetInMonthSample": "e.g. If the birth month is June, the campaign can be set to be active on 15th May",
        "offsetInMonthReminder": "If you select 29th, 30th, or 31st, and there is no related number of days in the month, the last day of that month will be automatically selected.",
        "offsetWeekdaySuffix": "month(s) before the first day of the birth month",
        "offsetWeekdaySample": "e.g. If the birth month is June, the campaign can be set to be active on the second Sunday of May"
    },
    end: {
        "title": "End date",
        "tips": "The default end time of the selected end date is 23:59.",
        "relativeTypeSuffix": "after the last day of the birth month",
        "offsetInMonthSuffix": "month(s) after the last day of the birth month",
        "offsetInMonthSample": "e.g. If the birth month is June, the campaign can be set to be active on 15th May",
        "offsetInMonthReminder": "If you select 29th, 30th, or 31st, and there is no related number of days in the month, the last day of that month will be automatically selected.",
        "offsetWeekdaySuffix": "month(s) after the last day of the birth month",
        "offsetWeekdaySample": "e.g. If the birth month is June, the campaign can be set to be active on the second Sunday of May"
    }
}

function getRecurringOptions(key) {
    return [
        {
            label: `Select a date ${key}\nthe birth month`,
            value: recurringRadioOptionTypes.OFFSET_BY_RELATIVE
        },
        {
            label: `Select one day in a month ${key}\nthe birth month`,
            value: recurringRadioOptionTypes.OFFSET_DAY_IN_MONTH
        },
        {
            label: `Select one day in a week ${key}\nthe birth month`,
            value: recurringRadioOptionTypes.OFFSET_WEEKDAY
        }
    ]
}

function getRecurringSelectedOption({ offsetType }) {
    switch (offsetType) {
        case RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_DAY:
        case RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_MONTH:
        case RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_WEEK:
            return recurringRadioOptionTypes.OFFSET_BY_RELATIVE
        case RECURRING_OFFSET_TYPE.OFFSET_DAY_IN_MONTH:
            return recurringRadioOptionTypes.OFFSET_DAY_IN_MONTH
        case RECURRING_OFFSET_TYPE.OFFSET_WEEKDAY:
            return recurringRadioOptionTypes.OFFSET_WEEKDAY
        default:
            return recurringRadioOptionTypes.OFFSET_BY_RELATIVE
    }
}

function getOffsetType({ selectedOption, relativeType }) {
    switch (selectedOption) {
        case recurringRadioOptionTypes.OFFSET_DAY_IN_MONTH:
            return RECURRING_OFFSET_TYPE.OFFSET_DAY_IN_MONTH
        case recurringRadioOptionTypes.OFFSET_WEEKDAY:
            return RECURRING_OFFSET_TYPE.OFFSET_WEEKDAY
        default:
            if (relativeType) {
                return relativeType
            }

            return RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_DAY
    }
}

function getRelativeValue({ offsetType, offsetDays, offsetMonths, offsetWeeks }) {
    switch (offsetType) {
        case RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_DAY:
            return offsetDays
        case RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_WEEK:
            return offsetWeeks
        case RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_MONTH:
            return offsetMonths
        default:
            return undefined
    }
}

function getValue({ valueName, watch, defaultValue, setValue }) {
    const watchValue = watch(valueName)
    if (!watchValue) {
        setValue(valueName, defaultValue, { shouldDirty: true })
        return defaultValue
    }
    return watchValue
}

function RelativeSelector({ offsetType, selectorPrefix, setValue, words, watch }) {
    if (
        ![RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_DAY,
        RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_WEEK,
        RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_MONTH
        ].includes(offsetType)
    ) {
        return null
    }

    const offsetDays = watch(`${selectorPrefix}OffsetDays`)
    const offsetMonths = watch(`${selectorPrefix}OffsetMonths`)
    const offsetWeeks = watch(`${selectorPrefix}OffsetWeeks`)
    const actions = relativeTypes.map(item => ({
        name: item.name,
        action: () => setValue(`${selectorPrefix}DateOffsetType`, item.value, { shouldDirty: true })
    }))
    const defaultRelativeType = relativeTypes.find(item => item.value == offsetType)
    const relativeValue = getRelativeValue({ offsetType, offsetDays, offsetMonths, offsetWeeks })
    return (
        <div className="relative-recurring-container">
            <input
                type="number"
                className={`custom-number-input-short margin-right-10-percent`}
                value={relativeValue}
                min={1}
                onChange={({ target }) => {
                    setValue(`${selectorPrefix}OffsetDays`, target.value || undefined, { shouldDirty: true })
                    setValue(`${selectorPrefix}OffsetMonths`, target.value || undefined, { shouldDirty: true })
                    setValue(`${selectorPrefix}OffsetWeeks`, target.value || undefined, { shouldDirty: true })
                }}

            />
            <DropDownWithDefaultSelect
                className='recurring-drop-down-selector'
                actions={actions}
                defaultValue={defaultRelativeType.name}
            />
            <label className="section-short-description">
                {words.relativeTypeSuffix}
            </label>
        </div>
    )
}

function SpecificDayType({ offsetType, selectorPrefix, setValue, words, watch }) {
    if (offsetType != RECURRING_OFFSET_TYPE.OFFSET_DAY_IN_MONTH) {
        return null
    }
    const actions = Array.from({ length: 31 }, (_, index) => ({
        name: getOrdinalSuffix(index + 1),
        action: () => setValue(`${selectorPrefix}SpecificDayInMonth`, index + 1, { shouldDirty: true })
    }));
    const offsetMonths = watch(`${selectorPrefix}OffsetMonths`)
    const specificDayInMonth = getValue({
        valueName: `${selectorPrefix}SpecificDayInMonth`,
        watch,
        defaultValue: 1,
        setValue
    })
    const defaultSpecificDay = getOrdinalSuffix(specificDayInMonth)

    return (
        <>
            <div className="relative-recurring-container">
                <label className="section-short-description">
                    On the
                </label>
                <DropDownWithDefaultSelect
                    className='recurring-drop-down-selector margin-left-10-percent specific-day-margin'
                    actions={actions}
                    defaultValue={defaultSpecificDay}
                />
                <CustomTips
                    popoverClassName="tips-popover"
                    detail={words.offsetInMonthReminder}
                />

                <label className="section-short-description">
                    of the
                </label>

                <input
                    type="number"
                    className={`custom-number-input-short margin-right-10-percent margin-left-10-percent`}
                    value={offsetMonths}
                    min={1}
                    onChange={({ target }) => {
                        setValue(`${selectorPrefix}OffsetMonths`, target.value || undefined, { shouldDirty: true })
                    }}

                />
                <label className="section-short-description">
                    {words.offsetInMonthSuffix}
                </label>
            </div>
            <label className="tips-message margin-top-8-percent">
                {words.offsetInMonthSample}
            </label>
        </>
    )
}

function WeekdayType({ offsetType, selectorPrefix, setValue, words, watch }) {
    if (offsetType != RECURRING_OFFSET_TYPE.OFFSET_WEEKDAY) {
        return null
    }
    const ordinalKeys = Object.keys(RECURRING_ORDINAL_WEEKDAY_TYPE)
    const weekdayKeys = Object.keys(WEEKDAY)
    const offsetMonths = watch(`${selectorPrefix}OffsetMonths`)
    const ordinalWeekdayInMonth = getValue({
        valueName: `${selectorPrefix}OrdinalWeekdayInMonth`,
        watch,
        defaultValue: ordinalKeys[0],
        setValue
    })

    const specificWeekday = getValue({
        valueName: `${selectorPrefix}SpecificWeekday`,
        watch,
        defaultValue: weekdayKeys[0],
        setValue
    })

    const defaultOrdinalWeekday = RECURRING_ORDINAL_WEEKDAY_TYPE[ordinalWeekdayInMonth]
    const defaultWeekday = WEEKDAY[specificWeekday]

    const ordinalWeekActions = ordinalKeys.map((item) => ({
        name: RECURRING_ORDINAL_WEEKDAY_TYPE[item],
        action: () => setValue(`${selectorPrefix}OrdinalWeekdayInMonth`, item, { shouldDirty: true })
    }));
    const weekdayActions = weekdayKeys.map((item) => ({
        name: WEEKDAY[item],
        action: () => setValue(`${selectorPrefix}SpecificWeekday`, item, { shouldDirty: true })
    }));

    return (
        <>
            <div className="relative-recurring-container">
                <label className="section-short-description">
                    On the
                </label>
                <DropDownWithDefaultSelect
                    className='recurring-drop-down-selector margin-left-10-percent'
                    actions={ordinalWeekActions}
                    defaultValue={defaultOrdinalWeekday}
                />

                <label className="section-short-description">
                    of the
                </label>
                <DropDownWithDefaultSelect
                    className='recurring-drop-down-selector margin-left-10-percent'
                    actions={weekdayActions}
                    defaultValue={defaultWeekday}
                />
                <label className="section-short-description">
                    of the
                </label>
                <input
                    type="number"
                    className={`custom-number-input-short margin-right-10-percent margin-left-10-percent`}
                    value={offsetMonths}
                    min={1}
                    onChange={({ target }) => {
                        setValue(`${selectorPrefix}OffsetMonths`, target.value || undefined, { shouldDirty: true })
                    }}

                />
                <label className="section-short-description">
                    {words.offsetWeekdaySuffix}
                </label>
            </div>
            <label className="tips-message margin-top-8-percent">
                {words.offsetWeekdaySample}
            </label>
        </>
    )
}

function PeriodSelector({
    words,
    selectorPrefix,
    hidden,
    watch,
    setValue,
    errors,
}) {
    if (hidden) {
        return null
    }
    const offsetType = getValue({
        valueName: `${selectorPrefix}DateOffsetType`,
        watch,
        defaultValue: RECURRING_OFFSET_TYPE.OFFSET_BY_RELATIVE_DAY,
        setValue
    })
    const optionChecked = getRecurringSelectedOption({ offsetType })
    const optionKey = selectorPrefix?.includes("Start") ? "before" : "after"
    const options = getRecurringOptions(optionKey)
    return <>
        <CustomTitleWithRadio
            title={words.title}
            options={options}
            tips={words.tips}
            defaultValue={optionChecked}
            setValue={(value) => {
                const changedOffsetType = getOffsetType({ selectedOption: value, offsetType })
                if (offsetType != changedOffsetType) {
                    setValue(`${selectorPrefix}OffsetMonths`, undefined, { shouldDirty: true })
                    setValue(`${selectorPrefix}OffsetDays`, undefined, { shouldDirty: true })
                    setValue(`${selectorPrefix}OffsetWeeks`, undefined, { shouldDirty: true })
                    setValue(`${selectorPrefix}SpecificWeekday`, undefined, { shouldDirty: true })
                }
                setValue(`${selectorPrefix}DateOffsetType`, changedOffsetType, { shouldDirty: true });
            }}
        />
        <RelativeSelector
            offsetType={offsetType}
            selectorPrefix={selectorPrefix}
            setValue={setValue}
            watch={watch}
            words={words}
        />
        <SpecificDayType
            offsetType={offsetType}
            selectorPrefix={selectorPrefix}
            setValue={setValue}
            watch={watch}
            words={words}
        />
        <WeekdayType
            offsetType={offsetType}
            selectorPrefix={selectorPrefix}
            setValue={setValue}
            watch={watch}
            words={words}
        />
        <ReactHookFormErrorMessage
            id={`${selectorPrefix}`}
            errors={errors}
        />
    </>
}

export default function RecurringCampaignPeriodSelector({ prefix }) {
    const dispatch = useDispatch()
    const formContext = useFormContext()
    const {
        setValue,
        watch,
        formState: { errors }
    } = formContext;
    const followBirthdayMonthFiledName = `${prefix}PeriodFollowBirthMonth`
    const periodFollowBirthMonth = watch(followBirthdayMonthFiledName)
    if (periodFollowBirthMonth == undefined) {
        setValue(followBirthdayMonthFiledName, true, { shouldDirty: true })
    }
    return <>
        <ReactHookFormSwitch
            title={"Follow birth month"}
            valueFieldName={followBirthdayMonthFiledName}
            tips={"Start at 00:00 on the 1st day of birth month.\nEnd at 23:59 on the last day of birth month."}
        />
        <PeriodSelector
            words={offsetTypeWords.start}
            hidden={periodFollowBirthMonth}
            selectorPrefix={`${prefix}Start`}
            watch={watch}
            setValue={setValue}
            errors={errors}
        />
        <PeriodSelector
            words={offsetTypeWords.end}
            hidden={periodFollowBirthMonth}
            selectorPrefix={`${prefix}End`}
            watch={watch}
            setValue={setValue}
            errors={errors}
        />
    </>
}